import React, { forwardRef } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Colors } from '../../constants/Colors';
import { useDynamicStyles } from '../../hooks/useDynamicStyles';
import { TextBold } from '../StyledText';

type TableHeadProps = ViewProps & {
  children?: React.ReactNode;
  flex?: number;
  width?: number;
  align?: 'left' | 'center' | 'right';
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
};

/**
 * TableHead - Table header cell
 *
 * Use inside TableRow within TableHeader.
 * Automatically styles text as bold.
 *
 * @example
 * ```tsx
 * <TableHead flex={2}>Name</TableHead>
 * <TableHead flex={1} align="center">Status</TableHead>
 * <TableHead width={100} align="right">Actions</TableHead>
 * ```
 */
export const TableHead = forwardRef<View, TableHeadProps>(
  (
    {
      children,
      style,
      flex,
      width,
      align = 'left',
      hideOnMobile,
      hideOnTablet,
      ...props
    },
    ref,
  ) => {
    const styles = useDynamicStyles(createStyles);

    const cellStyle = [
      styles.head,
      flex !== undefined && { flex },
      width !== undefined && { width },
      align === 'center' && styles.centerAlign,
      align === 'right' && styles.rightAlign,
      hideOnMobile && styles.hideOnMobile,
      hideOnTablet && styles.hideOnTablet,
      style,
    ];

    return (
      <View ref={ref} style={cellStyle} {...props}>
        {typeof children === 'string' ? (
          <TextBold style={styles.headText} numberOfLines={1}>
            {children}
          </TextBold>
        ) : (
          children
        )}
      </View>
    );
  },
);

TableHead.displayName = 'TableHead';

const createStyles = ({
  isMobile,
  isTablet,
}: {
  isMobile: boolean;
  isTablet: boolean;
}) =>
  StyleSheet.create({
    head: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      justifyContent: 'center',
    },
    headText: {
      fontSize: 14,
      color: Colors.text,
    },
    centerAlign: {
      alignItems: 'center',
    },
    rightAlign: {
      alignItems: 'flex-end',
    },
    hideOnMobile: {
      display: isMobile ? 'none' : 'flex',
    },
    hideOnTablet: {
      display: isTablet ? 'none' : 'flex',
    },
  });
